<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { VueDatePicker, WeekStart } from '@vuepic/vue-datepicker'
import '@vuepic/vue-datepicker/dist/main.css'
import type { StatisticsFilterEvent } from '@/components/dashboard/dashboard.interfaces.ts'
import dayjs from 'dayjs'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import CrdLabel from '@/components/common/CrdLabel.vue'
import { useTranslations } from '@/composables/useTranslations.ts'
const { t } = useTranslations()

dayjs.extend(isSameOrBefore)
dayjs.extend(isSameOrAfter)

const props = defineProps<{
  fromDate: Date | string
  toDate: Date | string
}>()

const emit = defineEmits<{ (e: 'update:filter', value: StatisticsFilterEvent): void }>()
const startDate = ref<Date | string>(props.fromDate ? new Date(props.fromDate) : '')
const endDate = ref<Date | string>(props.toDate ? new Date(props.toDate) : '')
const maxDate = ref(new Date())
const highlightedDates = ref<Date[]>([])
const setStartDate = (newStart: Date | string) => (startDate.value = newStart)
const setEndDate = (newEnd: Date | string) => (endDate.value = newEnd)

const endDateMax = computed(() => {
  if (!startDate.value) return maxDate.value
  const date = dayjs(startDate.value).add(3, 'month').toDate()
  return date > maxDate.value ? maxDate.value : date
})

function getDatesBetween(start: Date, end: Date): Date[] {
  const dates: Date[] = []
  let current = dayjs(start).startOf('day')
  const endDate = dayjs(end).startOf('day')

  while (current.isSameOrBefore(endDate)) {
    dates.push(current.toDate())
    current = current.add(1, 'day')
  }
  return dates
}

function resetFilter(): void {
  const defaultDates = {
    start: dayjs().subtract(3, 'month').format('YYYY-MM-DD'),
    end: dayjs().format('YYYY-MM-DD'),
  }

  setStartDate(new Date(defaultDates.start))
  setEndDate(new Date(defaultDates.end))
  emit('update:filter', { fromDate: defaultDates.start, toDate: defaultDates.end })
}

defineExpose({ resetFilter })

watch(
  [startDate, endDate],
  () => {
    emit('update:filter', {
      fromDate: startDate.value ? dayjs(startDate.value).format('YYYY-MM-DD') : '',
      toDate: endDate.value ? dayjs(endDate.value).format('YYYY-MM-DD') : '',
    })

    highlightedDates.value =
      startDate.value && endDate.value
        ? getDatesBetween(new Date(startDate.value), new Date(endDate.value))
        : []
  },
  { immediate: true },
)
</script>

<template>
  <div class="flex items-center gap-3">
    <div>
      <CrdLabel class="text-sm md:text-base font-semibold">{{ t('analytics.startDate') }}:</CrdLabel>
      <VueDatePicker
        v-model="startDate"
        :highlight="{ dates: highlightedDates }"
        disable-year-select
        :max-date="maxDate"
        :formats="{ input: 'dd.MM.yyyy' }"
        :time-config="{ enableTimePicker: false }"
        :input-attrs="{ clearable: true }"
        :week-start="WeekStart.Monday"
        auto-apply
        @cleared="startDate = ''"
      />
    </div>
    <div>
      <CrdLabel class="text-sm md:text-base font-semibold">{{ t('analytics.endDate') }}:</CrdLabel>
      <VueDatePicker
        v-model="endDate"
        :highlight="{ dates: highlightedDates }"
        disable-year-select
        :formats="{ input: 'dd.MM.yyyy' }"
        :time-config="{ enableTimePicker: false }"
        :input-attrs="{ clearable: true }"
        :week-start="WeekStart.Monday"
        :min-date="startDate"
        :max-date="endDateMax"
        :disabled="!startDate"
        auto-apply
        @cleared="endDate = ''"
      />
    </div>
  </div>
</template>

<style scoped>
:deep(.dp--input) {
  padding-top: 5px !important;
  padding-bottom: 5px !important;
}
</style>
